/**
 * Frontend permission helpers — mirrors backend/src/services/permissions.service.js
 */

import { formatInt } from "../utils/numberLocale";
import {
  calendarTypeLabel,
  normalizeCalendarType,
} from "../utils/calendarType";

export function getPermissions(jobDetails) {
  const perms = jobDetails?.permissions;
  return Array.isArray(perms) ? perms.map(String) : [];
}

export function hasPermission(jobDetails, required) {
  if (!required) return true;
  const perms = getPermissions(jobDetails);
  if (perms.includes("all")) return true;

  const needed = Array.isArray(required) ? required : [required];
  return needed.some((p) => perms.includes(String(p)));
}

/** *.view is implied by create/edit/delete on the same resource prefix */
export function hasPermissionOrImplied(jobDetails, required) {
  if (hasPermission(jobDetails, required)) return true;
  if (!required || typeof required !== "string") return false;

  const parts = required.split(".");
  if (parts.length < 2) return false;
  if (parts[parts.length - 1] !== "view") return false;

  const base = parts.slice(0, -1).join(".");
  return (
    hasPermission(jobDetails, `${base}.create`) ||
    hasPermission(jobDetails, `${base}.edit`) ||
    hasPermission(jobDetails, `${base}.delete`)
  );
}

export function canAccessPermission(jobDetails, required) {
  if (!required) return true;
  if (Array.isArray(required)) {
    return required.some((p) => canAccessPermission(jobDetails, p));
  }
  if (String(required).endsWith(".view")) {
    return hasPermissionOrImplied(jobDetails, required);
  }
  return hasPermission(jobDetails, required);
}

/**
 * Report Designer access (Sidebar + `/reports/designer` must use this).
 *
 * Preserves legacy admin visibility (`superuser` / `job_id >= 70`) so existing
 * admins are not silently locked out, and also allows `settings.edit` (and
 * therefore `permissions: ["all"]`) because designer save posts to settings.
 *
 * `settings.edit` alone is NOT equivalent to `job_id >= 70` in seeded jobs —
 * do not replace the legacy admin checks with only `settings.edit`.
 */
export function canAccessReportDesigner(profile) {
  if (!profile) return false;
  if (profile.national_code === "superuser") return true;
  const jobId = Number(profile.job_id);
  if (Number.isFinite(jobId) && jobId >= 70) return true;
  return canAccessPermission(profile.job_details, "settings.edit");
}

/**
 * Route path → required permission (Router only).
 * Navigation visibility uses job_details.sidemenu separately — see canSeeSidemenuKey.
 *
 * `/working-calendar` uses the same `settings.view` gate as `/settings`, then the
 * router opens Settings tab `working-calendar`. Sidemenu does not grant it.
 * `/reports/designer` is listed as settings.edit for documentation/parity with the
 * permission catalog; the live gate is canAccessReportDesigner (also allows legacy
 * superuser / job_id >= 70).
 */
export const ROUTE_PERMISSIONS = {
  "/": "dashboard.view",
  "/users": "users.view",
  "/reports": "reports.view",
  "/reports/designer": "settings.edit",
  "/settings": "settings.view",
  "/working-calendar": "settings.view",
  "/targets": "targets.view",
  "/targets/settings": "targets.view",
  "/commissions": "commissions.view",
  "/pool": "pool.view",
  "/audit": "audit.view",
};

/** Sidebar visibility: sidemenu membership only (never grant/deny routes). */
export function canSeeSidemenuKey(jobDetails, key) {
  return Array.isArray(jobDetails?.sidemenu) && jobDetails.sidemenu.includes(key);
}

/** Filter sidemenu catalog items by job_details.sidemenu (ignore item.permission). */
export function filterSidemenuItems(jobDetails, items) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => canSeeSidemenuKey(jobDetails, item?.key));
}

export function resolveTargetWindow(jobDetails, calendarType = "jalali") {
  if (
    hasPermission(jobDetails, "targets.anytime") ||
    hasPermission(jobDetails, "all")
  ) {
    return { mode: "anytime" };
  }

  const calendar = normalizeCalendarType(calendarType);
  const startField =
    calendar === "gregorian"
      ? "target_window_gregorian_start"
      : "target_window_jalali_start";
  const endField =
    calendar === "gregorian"
      ? "target_window_gregorian_end"
      : "target_window_jalali_end";

  const startRaw = jobDetails?.[startField];
  const endRaw = jobDetails?.[endField];
  const hasStart = startRaw !== undefined && startRaw !== null && startRaw !== "";
  const hasEnd = endRaw !== undefined && endRaw !== null && endRaw !== "";

  if (hasStart || hasEnd) {
    return {
      mode: "month_range",
      calendar,
      startDay: hasStart ? Number(startRaw) : -28,
      endDay: hasEnd ? Number(endRaw) : 10,
    };
  }

  return { mode: "anytime" };
}

export function isSuperuserProfile(profile) {
  if (!profile || typeof profile !== "object") return false;
  const jobId = Number(profile.job_id ?? profile.job_details?.id);
  const national = String(profile.national_code || "").toLowerCase();
  return jobId === 77 || national === "superuser";
}

function profileNotificationPrefs(profile) {
  const raw =
    profile?.customization?.notifications &&
    typeof profile.customization.notifications === "object"
      ? profile.customization.notifications
      : {};
  return {
    targetting: raw.targetting === true,
    targetting_jalali: raw.targetting_jalali === true,
    targetting_gregorian: raw.targetting_gregorian === true,
  };
}

/**
 * Whether the profile may bypass the role targeting window (top priority).
 * ON  → allowed for the current month even after the role window closed.
 * OFF → the role jalali/gregorian window is the only gate (never disables targeting).
 */
export function canBypassTargetWindow(profile, calendarType = "jalali") {
  if (isSuperuserProfile(profile)) return true;

  const calendar = normalizeCalendarType(calendarType);
  const column = calendar === "gregorian" ? "gregorian_bypass" : "jalali_bypass";
  if (profile && typeof profile === "object" && profile[column] === true) {
    return true;
  }

  const prefs = profileNotificationPrefs(profile);
  if (!prefs.targetting) return false;

  const raw = profile?.customization?.notifications;
  const key =
    calendar === "gregorian" ? "targetting_gregorian" : "targetting_jalali";
  if (
    !raw ||
    typeof raw !== "object" ||
    !Object.prototype.hasOwnProperty.call(raw, key)
  ) {
    return true;
  }
  return raw[key] === true;
}

/**
 * Whether a day-of-month falls inside a [startDay, endDay] targeting window.
 *
 * Bounds are day numbers relative to the 1st of the month:
 *   > 0 → that day of the month (20 = the 20th day), for both calendars
 *  <= 0 → that many days before the 1st, so the bound already passed before the
 *         month started and does not restrict the current month.
 *
 * A start greater than a positive end wraps inside the same month
 * (e.g. 20 → 10 means the 20th..end plus the 1st..10th).
 */
export function isDayInWindow(dayOfMonth, startDay, endDay) {
  const day = Number(dayOfMonth);
  const start = Number(startDay);
  const end = Number(endDay);
  if (!Number.isFinite(day)) return false;

  if (Number.isFinite(end) && end <= 0) return false;
  if (!Number.isFinite(end)) {
    return !(Number.isFinite(start) && start > 0) || day >= start;
  }

  const hasStart = Number.isFinite(start) && start > 0;
  if (hasStart && start > end) return day >= start || day <= end;
  return (!hasStart || day >= start) && day <= end;
}

/**
 * @param {object} jobDetails
 * @param {number} year
 * @param {number} month
 * @param {{ year: number, month: number, day: number }} todayParts
 * @param {(date?: Date) => { year: number, month: number, day: number }} toParts
 * @param {string} [calendarType]
 */
export function isYearMonthInTargetWindow(
  jobDetails,
  year,
  month,
  todayParts,
  toParts,
  calendarType = "jalali",
) {
  const calendar = normalizeCalendarType(calendarType);
  const window = resolveTargetWindow(jobDetails, calendar);
  if (window.mode === "anytime") return true;

  if (window.mode === "month_range") {
    // Only the current month is targetable.
    if (Number(year) !== todayParts.year || Number(month) !== todayParts.month) {
      return false;
    }
    return isDayInWindow(todayParts.day, window.startDay, window.endDay);
  }

  // Legacy modes should not be reached with new schema
  return false;
}

/**
 * Whether targeting mutations are allowed for the given year/month.
 *
 * Priority:
 * 1. Role "anytime" → always allowed.
 * 2. Profile toggle ON → bypass (top priority).
 * 3. Otherwise the role day window is the only gate.
 *
 * Toggle OFF never disables targeting; it just stops bypassing the role window.
 *
 * @param {object} jobDetails
 * @param {object} profile - user profile with customization / *_bypass
 * @param {number} year
 * @param {number} month
 * @param {{ year: number, month: number, day: number }} todayParts
 * @param {(date?: Date) => { year: number, month: number, day: number }} toParts
 * @param {string} [calendarType]
 */
export function canMutateTargets(
  jobDetails,
  profile,
  year,
  month,
  todayParts,
  toParts,
  calendarType = "jalali",
) {
  const calendar = normalizeCalendarType(calendarType);
  const window = resolveTargetWindow(jobDetails, calendar);

  if (window.mode === "anytime") {
    return { allowed: true, window, source: "role_anytime" };
  }

  // Toggle ON is top priority: it bypasses the role window.
  if (canBypassTargetWindow(profile, calendar)) {
    return { allowed: true, window, source: "user_bypass" };
  }

  const targetYear = year == null || year === "" ? todayParts.year : year;
  const targetMonth = month == null || month === "" ? todayParts.month : month;

  const allowed = isYearMonthInTargetWindow(
    jobDetails,
    targetYear,
    targetMonth,
    todayParts,
    toParts,
    calendar,
  );

  return {
    allowed,
    window,
    source: "role_window",
  };
}

export function describeTargetWindow(jobDetails, calendarType = "jalali") {
  const window = resolveTargetWindow(jobDetails, calendarType);
  if (window.mode === "anytime") {
    return "بدون محدودیت زمانی";
  }
  if (window.mode === "month_range") {
    const startDay = window.startDay;
    const endDay = window.endDay;
    const startLabel = startDay <= 0 
      ? `${formatInt(Math.abs(startDay))} روز قبل از ۱ ماه`
      : `روز ${formatInt(startDay)} ماه`;
    const endLabel = endDay <= 0 
      ? `${formatInt(Math.abs(endDay))} روز قبل از ۱ ماه`
      : `روز ${formatInt(endDay)} ماه`;
    return `از ${startLabel} تا ${endLabel} (${calendarTypeLabel(window.calendar)})`;
  }
  // Legacy modes should not be reached
  return "—";
}
